/**
 * A menu hung off a button: the user menu, the organization switcher, the
 * scheme and density controls, a row's overflow actions.
 *
 * ## Roving tabindex, not `tabindex="0"` on every item
 *
 * Exactly one item is in the tab order at a time and the arrow keys move which
 * one. This is what the menu role promises, and the promise is load-bearing:
 * declaring `role="menu"` and then leaving every item tabbable means a screen
 * reader announces "menu, 9 items" and then the arrow keys do nothing, so the
 * person is told about a structure that is not there. Tab leaves the menu
 * entirely, which is also what the role promises.
 *
 * ## Positioned by the layout, not by measurement
 *
 * The menu is absolutely positioned inside a wrapper next to the trigger, which
 * means it is clipped by any ancestor with `overflow: hidden`. The shell's
 * topbar and sidebar deliberately do not clip, for this reason.
 *
 * The alternative was the `popover` attribute, which puts the menu in the top
 * layer where nothing can clip it. It was rejected because without CSS anchor
 * positioning, which is still not something this product can rely on across the
 * browsers NGO staff actually have, the coordinates then have to be measured in
 * JavaScript and re-measured on every scroll and resize of every ancestor. That
 * is a large amount of machinery, and a class of bug where the menu drifts away
 * from its button, in exchange for solving a clipping problem the chrome does
 * not have.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'
import { CheckIcon } from '@/components/icons/Icons'
import styles from './DropdownMenu.module.css'

export type MenuItem =
  | {
      kind: 'action'
      id: string
      label: string
      icon?: ReactNode
      /** Trailing detail: a shortcut, a count, a role name. */
      note?: string
      onSelect(): void
      disabled?: boolean
      tone?: 'default' | 'danger'
    }
  | {
      kind: 'radio'
      id: string
      label: string
      icon?: ReactNode
      note?: string
      checked: boolean
      onSelect(): void
      disabled?: boolean
    }
  | { kind: 'separator'; id: string }
  | { kind: 'label'; id: string; label: string }

export interface DropdownMenuProps {
  /**
   * The trigger's accessible name.
   *
   * Constant. It says what the control is, never what it currently holds: a
   * button that renames itself "Dark" once dark is chosen cannot be found again
   * by anyone looking for the colour scheme, and cannot be queried for its
   * state either. State lives on `aria-checked` on the items.
   */
  label: string
  /** What the trigger shows. Icons inside it are already `aria-hidden`. */
  trigger: ReactNode
  items: readonly MenuItem[]
  /** Which edge the menu lines up with. `end` for anything on the right. */
  align?: 'start' | 'end'
  /** Opens upwards, for a trigger near the bottom of the window. */
  side?: 'bottom' | 'top'
  className?: string
  triggerClassName?: string
}

/** Items the arrow keys stop on. Separators and group labels are skipped. */
function isNavigable(item: MenuItem): boolean {
  return (item.kind === 'action' || item.kind === 'radio') && item.disabled !== true
}

export function DropdownMenu({
  label,
  trigger,
  items,
  align = 'start',
  side = 'bottom',
  className,
  triggerClassName,
}: DropdownMenuProps) {
  const [open, setOpen] = useState(false)
  const [activeId, setActiveId] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  const navigable = items.filter(isNavigable)

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false)
    setActiveId(null)
    if (restoreFocus) triggerRef.current?.focus()
  }, [])

  /*
   * `pointerdown`, not `click`. A click listener fires after the button that
   * was pressed has already run its own handler, so pressing a second menu's
   * trigger while this one is open opens and then immediately closes it.
   */
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: PointerEvent) {
      const target = event.target
      if (target instanceof Node && wrapperRef.current?.contains(target)) return
      // No focus restoration: the person is already on their way somewhere
      // else, and yanking focus back to the trigger undoes the click they made.
      setOpen(false)
      setActiveId(null)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [open])

  /*
   * The active item is what focus follows, so it has to actually be focused
   * after the menu renders rather than merely marked.
   *
   * Matched by walking the rendered items rather than by building an attribute
   * selector out of the id. Ids come from callers, and a project or account
   * name reaching this component as an id would put a quote or a bracket into
   * the selector and throw a SyntaxError out of a keypress handler.
   */
  useEffect(() => {
    if (!open || !activeId) return
    const menu = menuRef.current
    if (!menu) return
    const element = Array.from(menu.querySelectorAll<HTMLElement>('[data-item-id]')).find(
      (candidate) => candidate.dataset.itemId === activeId,
    )
    element?.focus()
  }, [open, activeId])

  function openWith(which: 'first' | 'last') {
    const target = which === 'first' ? navigable[0] : navigable[navigable.length - 1]
    setOpen(true)
    setActiveId(target ? target.id : null)
  }

  function onTriggerKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      openWith('first')
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      openWith('last')
    }
  }

  function move(delta: number) {
    if (navigable.length === 0) return
    const index = navigable.findIndex((item) => item.id === activeId)
    // Wraps. A menu that stops dead at the last item makes reaching "Sign out"
    // from the top a count-the-presses exercise.
    const next = navigable[(index + delta + navigable.length) % navigable.length]
    if (next) setActiveId(next.id)
  }

  function onMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        move(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        move(-1)
        break
      case 'Home': {
        event.preventDefault()
        const first = navigable[0]
        if (first) setActiveId(first.id)
        break
      }
      case 'End': {
        event.preventDefault()
        const last = navigable[navigable.length - 1]
        if (last) setActiveId(last.id)
        break
      }
      case 'Escape':
        event.preventDefault()
        close(true)
        break
      case 'Tab':
        // Not prevented. Tab moves on to the next control on the page, which is
        // what the menu role means, and the menu closes behind it.
        close(false)
        break
      default:
        break
    }
  }

  function select(item: MenuItem) {
    if (item.kind !== 'action' && item.kind !== 'radio') return
    if (item.disabled) return
    item.onSelect()
    close(true)
  }

  const wrapperClassName = [styles.wrapper, className].filter(Boolean).join(' ')

  return (
    <div className={wrapperClassName} ref={wrapperRef}>
      <button
        type="button"
        ref={triggerRef}
        className={[styles.trigger, triggerClassName].filter(Boolean).join(' ')}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? close(false) : openWith('first'))}
        onKeyDown={onTriggerKeyDown}
      >
        {trigger}
      </button>

      {open ? (
        <div
          id={menuId}
          ref={menuRef}
          className={styles.menu}
          data-align={align}
          data-side={side}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
        >
          {items.map((item) => {
            if (item.kind === 'separator') {
              return <hr key={item.id} className={styles.separator} />
            }

            if (item.kind === 'label') {
              return (
                <p key={item.id} className={styles.groupLabel}>
                  {item.label}
                </p>
              )
            }

            const isRadio = item.kind === 'radio'
            const itemClassName = [
              styles.item,
              item.kind === 'action' && item.tone === 'danger' ? styles.danger : '',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <button
                key={item.id}
                type="button"
                data-item-id={item.id}
                className={itemClassName}
                role={isRadio ? 'menuitemradio' : 'menuitem'}
                aria-checked={isRadio ? item.checked : undefined}
                disabled={item.disabled}
                // Roving tabindex: one stop for the whole menu.
                tabIndex={item.id === activeId ? 0 : -1}
                onClick={() => select(item)}
                onMouseEnter={() => setActiveId(item.id)}
              >
                <span className={styles.itemIcon} aria-hidden>
                  {item.icon ?? null}
                </span>
                <span className={styles.itemLabel}>{item.label}</span>
                {item.note ? <span className={styles.itemNote}>{item.note}</span> : null}
                {/* The chosen option is marked with a shape as well as with a
                    background tint, so the choice survives being printed, being
                    read in high contrast, and being looked at by somebody who
                    cannot separate the tint from the surface. */}
                <span className={styles.itemCheck} aria-hidden>
                  {isRadio && item.checked ? <CheckIcon size={16} /> : null}
                </span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
